import React, { useMemo } from 'react';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import BandwidthPanelExportShell from './BandwidthPanelExportShell';
import { exportBandwidthLabelCountExcel } from './bandwidthDashboardExcelExport';
import { exportBandwidthLabelCountPdf } from './bandwidthDashboardPdfExport';
import { bandwidthVerticalBarPalette } from './bandwidthChartTheme';
import { formatAxisCompactK } from '../kpi-dashboard/chartFormatters';
import { niceCountAxisMax } from './bandwidthChartMath';

export interface BandwidthHorizontalActivityCountBarRow {
  label: string;
  count: number;
  /** When set, row toggles Users chart drill on click. */
  drillValue?: string;
}

export interface BandwidthHorizontalActivityCountBarProps {
  title: string;
  subtitle?: string;
  rows: BandwidthHorizontalActivityCountBarRow[];
  emptyHint?: string;
  /** Selected drill value (activity type or customer id) for highlight / toggle-off. */
  selectedDrillValue?: string | null;
  onDrillRowClick?: (drillValue: string) => void;
  /** When set, shows Export → PDF / Excel for the current rows. */
  exportActions?: { filePrefix: string; rowHeader: string; valueHeader: string; dateFrom: string; dateTo: string };
}

/** Plot area height; only rows above the axis scroll. */
const SCROLL_PLOT_HEIGHT_CLASS = 'h-[280px]';

const TICKS = 5;

const BandwidthHorizontalActivityCountBar: React.FC<BandwidthHorizontalActivityCountBarProps> = ({
  title,
  subtitle,
  rows,
  emptyHint,
  selectedDrillValue = null,
  onDrillRowClick,
  exportActions,
}) => {
  const { tickValues, fullLabels, displayLabels, percents, colors } = useMemo(() => {
    const counts = rows.map((r) => r.count);
    const maxVal = counts.length ? Math.max(...counts) : 0;
    const maxScaleInner = niceCountAxisMax(maxVal);
    const tickValuesInner = Array.from({ length: TICKS }, (_, i) => (maxScaleInner * i) / (TICKS - 1));
    const fullLabelsInner = rows.map((r) => r.label);
    const displayLabelsInner = fullLabelsInner.map((s) => (s.length > 36 ? `${s.slice(0, 33)}…` : s));
    const percentsInner = rows.map((r) =>
      maxScaleInner > 0 ? Math.min(100, (r.count / maxScaleInner) * 100) : 0
    );
    const colorsInner = rows.map((_, i) => bandwidthVerticalBarPalette(i));
    return {
      tickValues: tickValuesInner,
      fullLabels: fullLabelsInner,
      displayLabels: displayLabelsInner,
      percents: percentsInner,
      colors: colorsInner,
    };
  }, [rows]);

  const gridStyle = useMemo(
    () => ({
      backgroundImage: `repeating-linear-gradient(
        to right,
        transparent 0,
        transparent calc(25% - 1px),
        rgba(148, 163, 184, 0.4) calc(25% - 1px),
        rgba(148, 163, 184, 0.4) 25%
      )`,
    }),
    []
  );

  const titleEnd =
    exportActions && rows.length > 0 ? (
      <BandwidthPanelExportShell
        disabled={false}
        ariaLabel={`Export ${title}`}
        buildPdf={() =>
          exportBandwidthLabelCountPdf(
            rows.map((r) => ({ label: r.label, count: r.count })),
            `Bandwidth dashboard — ${title}`,
            exportActions.filePrefix,
            exportActions.rowHeader,
            exportActions.valueHeader,
            exportActions.dateFrom,
            exportActions.dateTo
          )
        }
        buildExcel={() =>
          exportBandwidthLabelCountExcel(
            rows.map((r) => ({ label: r.label, count: r.count })),
            exportActions.rowHeader,
            exportActions.valueHeader,
            exportActions.filePrefix,
            title.slice(0, 28),
            exportActions.dateFrom,
            exportActions.dateTo
          )
        }
      />
    ) : undefined;

  if (rows.length === 0) {
    return (
      <KpiChartPanel
        title={title}
        titleClassName="!pl-0"
        className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[480px]"
        flexFill
        bodyClassName="px-3 pb-4 pt-4"
      >
        {subtitle ? <p className="mb-2 text-xs leading-snug text-slate-500">{subtitle}</p> : null}
        <p className="py-12 text-center text-sm text-slate-500">
          {emptyHint ?? 'No data for the selected filters.'}
        </p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      titleEnd={titleEnd}
      className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[480px]"
      flexFill
      bodyClassName="flex min-h-0 flex-1 flex-col px-3 pb-3 pt-3"
    >
      {subtitle ? <p className="mb-1 shrink-0 text-xs leading-snug text-slate-500">{subtitle}</p> : null}
      {onDrillRowClick ? (
        <p className="mb-1 shrink-0 text-[11px] text-slate-500">
          Click a row to filter other charts. Click the same row again to clear.
        </p>
      ) : null}

      {/* Scroll: category labels + bars only — x-axis is NOT inside this region */}
      <div
        className={`${SCROLL_PLOT_HEIGHT_CLASS} shrink-0 overflow-y-auto overflow-x-hidden rounded-lg border border-indigo-100/60 bg-gradient-to-br from-slate-50/90 to-violet-50/20 pr-1`}
      >
        <div className="flex min-w-0 flex-col gap-2 py-1 pl-0.5 pr-0">
            {rows.map((row, i) => {
              const dv = row.drillValue;
              const clickable = Boolean(onDrillRowClick && dv);
              const selected = Boolean(dv && selectedDrillValue === dv);
              const rowCls = `flex min-h-[28px] items-center gap-2 rounded-md px-0.5 py-0.5 ${
                clickable ? 'cursor-pointer transition-colors hover:bg-slate-100/90' : ''
              } ${selected ? 'bg-indigo-50/90 ring-1 ring-[#4472C4]/40' : ''}`;
              const inner = (
                <>
                  <div
                    className="w-[9rem] shrink-0 truncate text-left text-[11px] leading-tight text-slate-600"
                    title={fullLabels[i]}
                  >
                    {displayLabels[i]}
                  </div>
                  <div className="relative min-w-0 flex-1">
                    <div className="relative h-4 overflow-hidden rounded-md" style={gridStyle}>
                      <div
                        className="h-full rounded-md shadow-sm"
                        style={{
                          width: `${percents[i]}%`,
                          minWidth: row.count > 0 ? 2 : 0,
                          backgroundColor: colors[i],
                          opacity: selectedDrillValue && !selected ? 0.45 : 1,
                        }}
                      />
                    </div>
                  </div>
                  <div className="w-14 shrink-0 text-right text-[11px] font-semibold tabular-nums text-slate-800">
                    {row.count.toLocaleString()}
                  </div>
                </>
              );
              return clickable && dv ? (
                <button
                  key={`${row.label}-${i}`}
                  type="button"
                  className={`${rowCls} w-full border-0 bg-transparent text-left`}
                  onClick={() => onDrillRowClick!(dv)}
                >
                  {inner}
                </button>
              ) : (
                <div key={`${row.label}-${i}`} className={rowCls}>
                  {inner}
                </div>
              );
            })}
        </div>
      </div>

      {/* Fixed x-axis: always visible, does not scroll with bars */}
      <div className="mt-1 shrink-0 border-t border-indigo-100/60 bg-white pt-1.5">
        <div className="flex gap-2">
          <div className="w-[9rem] shrink-0" aria-hidden />
          <div className="relative flex min-w-0 flex-1 justify-between px-0 text-[10px] tabular-nums text-slate-500">
            {tickValues.map((t) => (
              <span key={t}>{formatAxisCompactK(t)}</span>
            ))}
          </div>
          <div className="w-14 shrink-0" aria-hidden />
        </div>
        <p className="mt-0.5 text-center text-[11px] font-medium text-slate-500">Count</p>
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthHorizontalActivityCountBar;
